import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { Input, NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import { TrashIcon } from '@heroicons/react/24/outline';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useAlias } from '@/lib/platform/hooks/use-alias';
import { AddEntryButton } from './AddEntryButton';

interface PlayerSale {
  player_name: string;
  sale_value: number;
}

interface PlayerSalesSectionProps {
  playerSales: PlayerSale[];
  onAddPlayerSale: () => void;
  onUpdatePlayerSale: (
    index: number,
    field: 'player_name' | 'sale_value',
    value: string | number
  ) => void;
  onDeletePlayerSale: (index: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function PlayerSalesSection({
  playerSales,
  onAddPlayerSale,
  onUpdatePlayerSale,
  onDeletePlayerSale,
  canEdit,
  numberFormat = 'eu',
}: PlayerSalesSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const { getLabel } = useAlias();

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          {getLabel('transfer_value_title')}
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {getLabel('transfer_value_description')}
          <Tooltip {...tooltipData.playerSales} wide={true} />
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="max-w-2xl md:col-span-2">
        {/* Player Sales List — border-only cards (same format as media entries) */}
        {playerSales.length > 0 && (
          <div className="space-y-8">
            {playerSales.map((sale, index) => (
              <div
                key={index}
                className={`space-y-2 rounded-lg border p-4 ${
                  darkMode ? 'border-white/10' : 'border-gray-200'
                }`}
              >
                {/* Line 1: player name + delete */}
                <div className="flex items-end gap-2">
                  {/* data-ai-field tags match the AI form bridge names (ValuationForm registration) */}
                  <div
                    className="min-w-0 flex-1"
                    data-ai-field={`player_sales[${index}].player_name`}
                  >
                    <Input
                      type="text"
                      label="Player Name"
                      value={sale.player_name}
                      onChange={(e) => onUpdatePlayerSale(index, 'player_name', e.target.value)}
                      placeholder="Enter player name"
                      disabled={!canEdit}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeletePlayerSale(index)}
                    disabled={!canEdit}
                    aria-label={`Delete player sale ${index + 1}`}
                    className={`shrink-0 rounded-lg p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      darkMode
                        ? 'text-gray-500 hover:bg-white/5 hover:text-red-400'
                        : 'text-gray-400 hover:bg-gray-50 hover:text-red-500'
                    }`}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                {/* Line 2: sale value */}
                <div
                  className="grid grid-cols-2 gap-2"
                  data-ai-field={`player_sales[${index}].sale_value`}
                >
                  <NumberInput
                    label="Sale Value"
                    suffix={getCurrencySymbol(baseCurrency)}
                    value={sale.sale_value}
                    onChange={(value) =>
                      onUpdatePlayerSale(index, 'sale_value', typeof value === 'number' ? value : 0)
                    }
                    placeholder="0"
                    disabled={!canEdit}
                    numberFormat={numberFormat}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Player Sale — adds the first/next line */}
        {playerSales.length > 0 ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={onAddPlayerSale}
              disabled={!canEdit}
              className={`w-full px-3 py-2 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                darkMode
                  ? 'text-indigo-400 hover:text-indigo-300'
                  : 'text-indigo-600 hover:text-indigo-500'
              }`}
            >
              + Add Player Sale
            </button>
          </div>
        ) : (
          <AddEntryButton label="Player Sale" onClick={onAddPlayerSale} disabled={!canEdit} />
        )}
      </div>
    </div>
  );
}
